// Env pinning: every player belongs to one env (prod, beta, ...), and the load balancer routes on ?env=.

/** Where the player's env is kept between launches (Preferences in the app, a fake in tests). */
export interface EnvStore {
  /** Resolves undefined when nothing is stored. */
  get(): Promise<string | undefined>;
  set(env: string): Promise<void>;
}

const ENV_PATTERN = /^[a-z][a-z0-9-]{0,39}$/;

export function isEnv(value: unknown): value is string {
  return typeof value === "string" && ENV_PATTERN.test(value);
}

/** Sets `env` as a query parameter of `url`, keeping any query it already has. */
export function withEnv(url: string, env: string | undefined): string {
  if (env === undefined) return url;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    // Left for the WebSocket constructor to reject.
    return url;
  }
  parsed.searchParams.set("env", env);
  return parsed.toString();
}

/**
 * The env to connect with: the stored pin, else the build's default, else none (first launch;
 * the load balancer picks one). Kept in memory so the socket URL can be built synchronously.
 */
export class EnvPin {
  private stored: string | undefined;

  constructor(
    private readonly store: EnvStore,
    private readonly fallback?: string,
  ) {}

  get current(): string | undefined {
    return this.stored ?? this.fallback;
  }

  /** Reads the stored pin; an unreadable or invalid value counts as none. */
  async load(): Promise<void> {
    try {
      const value = await this.store.get();
      if (this.stored === undefined && isEnv(value)) this.stored = value;
    } catch (err) {
      console.warn("reading the stored env failed", err);
    }
  }

  /** Pins `env` in memory right away and persists it in the background. Returns false for an invalid env. */
  set(env: unknown): boolean {
    if (!isEnv(env)) return false;
    if (env === this.stored) return true;
    this.stored = env;
    this.store.set(env).catch((err: unknown) => console.error("storing the env failed", err));
    return true;
  }
}
